import React from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography } from '@mui/material';
import { cardSx, iconTileSx, FONT } from '../styles/wizardSx';

/**
 * Shared building blocks so the four wizard steps look the same: a card with
 * an optional title row, and an empty state. The step title itself is drawn
 * once by AutomationWizard above the scroll area.
 */

export function SectionCard({ icon, title, helper, action, highlighted, children, sx, contentSx }) {
  const hasHeader = Boolean(title || action);
  return (
    <Paper
      elevation={0}
      sx={[(theme) => cardSx(theme, { highlighted }), ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {hasHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.5,
            py: 1.75,
            borderBottom: '1px solid',
            borderColor: 'outline.variant',
            bgcolor: 'surface.main',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
          }}
        >
          {icon && <Box sx={(theme) => iconTileSx(theme, 32)}>{icon}</Box>}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {title && (
              <Typography sx={{ fontSize: FONT.title, fontWeight: 600, color: 'text.primary' }}>
                {title}
              </Typography>
            )}
            {helper && (
              <Typography sx={{ fontSize: FONT.caption, color: 'text.secondary' }}>
                {helper}
              </Typography>
            )}
          </Box>
          {action}
        </Box>
      )}
      <Box sx={[{ p: 2.5 }, ...(Array.isArray(contentSx) ? contentSx : [contentSx])]}>
        {children}
      </Box>
    </Paper>
  );
}

SectionCard.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.node,
  helper: PropTypes.node,
  action: PropTypes.node,
  highlighted: PropTypes.bool,
  children: PropTypes.node,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
  contentSx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
};

/** Label over value, used by the review summary. */
export function SummaryField({ label, children }) {
  return (
    <Box>
      <Typography
        component="div"
        sx={{ fontSize: FONT.caption, fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
      >
        {label}
      </Typography>
      <Box sx={{ fontSize: FONT.body, color: 'text.primary' }}>{children}</Box>
    </Box>
  );
}

SummaryField.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node,
};

export function EmptyState({ icon, title, description, action, compact }) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: compact ? 3 : 6,
        px: 3,
        borderRadius: '12px',
        border: '2px dashed',
        borderColor: 'outline.variant',
        bgcolor: 'surface.main',
      }}
    >
      {icon && (
        <Box
          sx={(theme) => ({
            ...iconTileSx(theme, compact ? 44 : 64),
            borderRadius: '50%',
            mx: 'auto',
            mb: compact ? 1.5 : 2.5,
            '& svg': { fontSize: compact ? 22 : 32 },
          })}
        >
          {icon}
        </Box>
      )}
      <Typography
        sx={{ fontSize: compact ? FONT.body : FONT.title, fontWeight: 600, color: 'text.primary', mb: 0.5 }}
      >
        {title}
      </Typography>
      {description && (
        <Typography sx={{ fontSize: FONT.body, color: 'text.secondary', mb: action ? 2.5 : 0 }}>
          {description}
        </Typography>
      )}
      {action}
    </Box>
  );
}

EmptyState.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  action: PropTypes.node,
  compact: PropTypes.bool,
};
